/**
 * Google's rating for the company whose pin is open.
 *
 * Fetched when the card opens and thrown away when it closes, because that is
 * the only thing the Places policy allows: the place id may be kept forever,
 * the rating may not be kept at all. See `crm-maps/lib/places.ts` on the server
 * for the whole rule and what it costs us.
 *
 * The link to Google Maps is not decoration either. The policy requires giving
 * the reader a way through to the source, so a rating shown without it would be
 * a rating shown against the terms.
 */
import { useEffect, useState } from "react";
import { ExternalLink, Loader2, Star } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getCompanyPlace, type CompanyPlace } from "../../../../lib/companyMap";

/**
 * Below this, a company is worth calling about its reputation.
 *
 * A line, not a verdict: 4.0 is where Estonian buyers start reading the reviews
 * instead of the number, which is exactly the moment a business owner starts
 * caring. Only used to tint the star.
 */
const WEAK_RATING = 4.0;

/** Too few reviews to mean anything, whatever the average says. */
const THIN_REVIEWS = 5;

/**
 * One decimal, Estonian punctuation.
 *
 * `toFixed(1)` would print "3.2" beside a turnover printed as "4,8 mln", and
 * two decimal separators on one card read as two different kinds of number.
 * The locale is fixed rather than following the interface language for the same
 * reason `ui/format.ts` fixes it: these figures sit next to register extracts.
 */
const RATING_FMT = new Intl.NumberFormat("et-EE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function GoogleRating({ regCode }: { regCode: string }) {
  const t = useT();
  const [state, setState] = useState<CompanyPlace | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    setState(null);
    getCompanyPlace(regCode, ac.signal)
      .then((r) => {
        if (!ac.signal.aborted) setState(r);
      })
      .catch(() => {
        if (!ac.signal.aborted) setState({ ok: false, reason: "error" });
      });
    return () => ac.abort();
  }, [regCode]);

  if (!state) {
    return (
      <div className="crm-mut mt-2.5 flex items-center gap-1.5 text-[12px]">
        <Loader2 className="size-3.5 animate-spin" />
        {t("ariregister.map.googleLoading")}
      </div>
    );
  }

  // No key configured is the operator's own setup, not a fact about the
  // company, so it says nothing here rather than reading as "no reviews".
  if (!state.ok) {
    if (state.reason === "no_key") return null;
    return (
      <div className="crm-mut mt-2.5 text-[12px]">
        {state.reason === "not_found"
          ? t("ariregister.map.googleUnknown")
          : t("ariregister.map.googleFailed")}
      </div>
    );
  }

  if (state.rating == null) {
    return (
      <div className="crm-mut mt-2.5 text-[12px]">
        {t("ariregister.map.googleNoRating")}
        {state.googleMapsUri ? <> {" "}<MapsLink href={state.googleMapsUri} label={t("ariregister.map.googleOpen")} /></> : null}
      </div>
    );
  }

  const thin = (state.reviewCount ?? 0) < THIN_REVIEWS;
  const weak = state.rating < WEAK_RATING;

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
      <Star
        className="size-3.5 shrink-0"
        // Filled amber when the rating is a reason to call. Never red: a company
        // with a poor rating is a prospect here, not a problem.
        style={{ color: weak ? "#d97706" : "#64748b" }}
        fill={weak ? "#d97706" : "none"}
      />
      <span className="crm-tnum font-[650]">{RATING_FMT.format(state.rating)}</span>
      <span className="crm-mut">
        {state.reviewCount == null
          ? t("ariregister.map.googleReviewsUnknown")
          : t("ariregister.map.googleReviews", { n: state.reviewCount })}
      </span>
      {thin ? <span className="crm-mut">{t("ariregister.map.googleThin")}</span> : null}
      {state.googleMapsUri ? (
        <MapsLink href={state.googleMapsUri} label={t("ariregister.map.googleOpen")} />
      ) : null}
    </div>
  );
}

/** The way through to the source that the Places policy asks for. */
function MapsLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
    >
      {label}
      <ExternalLink className="size-3" />
    </a>
  );
}
